import { useLoaderData, Link } from 'react-router-dom'
import { format } from 'date-fns'
import { ArrowLeft, Calendar, User, Clock, ChevronRight } from 'lucide-react'
import { type WPPost } from '../../api/wordpress'
import { BlogNotFoundPage } from './BlogNotFoundPage'
import { BlogSEO } from '../../components/blog/BlogSEO'

interface LoaderData {
  post: WPPost
  recentPosts: WPPost[]
}

export function BlogPostPage() {
  const loaderData = useLoaderData() as LoaderData | undefined
  const post = loaderData?.post
  const recentPosts = loaderData?.recentPosts ?? []

  if (!post) return <BlogNotFoundPage />

  function getFeaturedImage(p: WPPost): string | null {
    if (p.featured_image_url) return p.featured_image_url
    return p._embedded?.['wp:featuredmedia']?.[0]?.source_url || null
  }

  function getAuthor(p: WPPost) {
    if (p.author_details) return { name: p.author_details.name, avatar: p.author_details.avatar, bio: p.author_details.bio }
    const embedded = p._embedded?.author?.[0]
    return { name: embedded?.name || 'Wedsy', avatar: embedded?.avatar_urls?.['96'], bio: '' }
  }

  function getPostCategories(p: WPPost): string[] {
    const terms = p._embedded?.['wp:term']?.[0]
    return terms?.filter(t => t.taxonomy === 'category').map(t => t.name) || []
  }

  function getReadingTime(p: WPPost): number {
    if (p.reading_time) return p.reading_time
    const content = p.content.rendered.replace(/<[^>]*>/g, '')
    return Math.ceil(content.split(/\s+/).length / 200)
  }

  const featuredImage = getFeaturedImage(post)
  const author = getAuthor(post)
  const postCategories = getPostCategories(post)
  const readingTime = getReadingTime(post)

  return (
    <article className="min-h-screen">
      <BlogSEO type="post" post={post} />
      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 pt-4 md:pt-6">
        <nav className="flex items-center gap-2 text-sm text-text-secondary">
          <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-text-primary truncate max-w-xs" dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
        </nav>
      </div>

      {/* Header */}
      <header className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {postCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {postCategories.map(cat => (
              <span key={cat} className="px-3 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary">{cat}</span>
            ))}
          </div>
        )}
        <h1
          className="blog-post-title mb-6"
          dangerouslySetInnerHTML={{ __html: post.title.rendered }}
        />
        <div className="flex flex-wrap items-center gap-4 text-sm text-text-secondary">
          <div className="flex items-center gap-2">
            {author.avatar ? <img src={author.avatar} alt={author.name} className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center"><User className="w-4 h-4 text-primary" /></div>}
            <span className="font-medium text-text-primary">{author.name}</span>
          </div>
          <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /><time dateTime={post.date} suppressHydrationWarning>{format(new Date(post.date), 'MMM d, yyyy')}</time></div>
          <div className="flex items-center gap-1.5"><Clock className="w-4 h-4" /><span>{readingTime} min read</span></div>
        </div>
      </header>

      {/* Featured image */}
      {featuredImage && (
        <div className="max-w-5xl mx-auto px-4 mb-8">
          <div className="aspect-[16/9] md:aspect-[2/1] rounded-2xl overflow-hidden bg-background-elevated">
            <img src={featuredImage} alt={post.title.rendered.replace(/<[^>]*>/g, '')} className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 pb-16">
        <div className="blog-article" dangerouslySetInnerHTML={{ __html: post.content.rendered }} suppressHydrationWarning />

        {/* Author card */}
        <div className="mt-12 p-6 rounded-xl bg-background-card border border-border">
          <div className="flex items-start gap-4">
            {author.avatar ? (
              <img src={author.avatar} alt={author.name} className="w-14 h-14 rounded-full flex-shrink-0 object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <User className="w-7 h-7 text-primary" />
              </div>
            )}
            <div>
              <div className="text-xs text-text-secondary mb-1">Written by</div>
              <div className="font-semibold text-text-primary">{author.name}</div>
              {author.bio && <p className="text-sm text-text-secondary mt-2">{author.bio}</p>}
            </div>
          </div>
        </div>

        {/* Related posts */}
        {recentPosts.length > 0 && (
          <div className="mt-12 pt-8 border-t border-border">
            <h3 className="text-lg font-semibold text-text-primary mb-6">More from the blog</h3>
            <div className="grid gap-4 md:grid-cols-3">
              {recentPosts.map(recentPost => {
                const img = getFeaturedImage(recentPost)
                const cats = getPostCategories(recentPost)
                return (
                  <article key={recentPost.id} className="group bg-background-card rounded-xl border border-border overflow-hidden hover:border-primary/30 hover:shadow-lg transition-all duration-300">
                    <Link to={`/blog/${recentPost.slug}`} className="block">
                      {img && <div className="aspect-[16/10] overflow-hidden bg-background-elevated"><img src={img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
                      <div className="p-4">
                        {cats.length > 0 && <div className="flex gap-2 mb-2">{cats.slice(0, 1).map(cat => <span key={cat} className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-primary/10 text-primary">{cat}</span>)}</div>}
                        <h4 className="text-sm font-semibold text-text-primary group-hover:text-primary transition-colors line-clamp-2" dangerouslySetInnerHTML={{ __html: recentPost.title.rendered }} />
                        <time className="text-xs text-text-secondary mt-2 block" dateTime={recentPost.date} suppressHydrationWarning>{format(new Date(recentPost.date), 'MMM d, yyyy')}</time>
                      </div>
                    </Link>
                  </article>
                )
              })}
            </div>
          </div>
        )}

        <div className="mt-12">
          <Link to="/blog" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to all posts
          </Link>
        </div>
      </div>
    </article>
  )
}
